/**
 * The Gantt's axis, from the workbench's timeline renderer (mju-ai-workbench
 * shared/diagrams/render/timeline.ts, its date scale): where an item is on
 * the axis, the axis itself (whole months either side, how wide a day is),
 * the month and quarter ticks, and the words for a day and a month. Copied
 * as they are; the renderer's SVG strings (the pen, the frame, the hand-drawn
 * look) are not, because ./GanttChart.tsx draws the same marks in React with
 * the console's own tokens.
 *
 * Pure: no Node, no DOM.
 */

import { roadmapTargetEnd } from "@aioe/shared/roadmap/model";
import { dateMedium, monthName, monthYear } from "@aioe/shared/wording/dateTime";
import type { TimelineItem } from "./ganttSpec.ts";

// ── date scale: the Gantt ─────────────────────────────────────────────

const DAY = 86_400_000;

export function dayOf(iso: string): number | undefined {
  const t = Date.parse(`${iso}T00:00:00Z`);
  return Number.isFinite(t) ? Math.floor(t / DAY) : undefined;
}

/** The last day a target means: a month's last day, a quarter's last day, or the day. */
export function targetDay(target: string): number | undefined {
  const end = roadmapTargetEnd(target);
  return end ? dayOf(end) : undefined;
}

const monthStart = (y: number, m: number) => Math.floor(Date.UTC(y, m, 1) / DAY);

/** Where an item is on the axis: a bar from start to end, or a diamond at one day. */
export interface GanttSpan {
  from: number;
  to: number;
  bar: boolean;
}

export function ganttSpan(item: TimelineItem): GanttSpan | undefined {
  if (item.more) return undefined;
  const start = item.start ? dayOf(item.start) : undefined;
  const end = item.end ? dayOf(item.end) : item.target ? targetDay(item.target) : undefined;
  if (start !== undefined && end !== undefined && !item.milestone) return { from: Math.min(start, end), to: Math.max(start, end), bar: true };
  const at = end ?? start;
  return at === undefined ? undefined : { from: at, to: at, bar: false };
}

/** The axis: whole months either side of what is drawn and of today, and how wide a day is. */
export interface GanttAxis {
  lo: number;
  hi: number;
  pxPerDay: number;
  /** Month ticks drawn (a month is wide enough for its name), else quarters only. */
  months: boolean;
}

export function ganttAxis(spans: readonly GanttSpan[], now: number | undefined, hand: boolean): GanttAxis {
  const days = [...spans.flatMap((s) => [s.from, s.to]), ...(now !== undefined ? [now] : [])];
  const loD = new Date(Math.min(...days) * DAY);
  const hiD = new Date(Math.max(...days) * DAY);
  const lo = monthStart(loD.getUTCFullYear(), loD.getUTCMonth());
  const hi = monthStart(hiD.getUTCFullYear(), hiD.getUTCMonth() + 1);
  const span = Math.max(28, hi - lo);
  // About 900 px of chart whatever the span; a month never narrower than 30 px.
  const pxPerDay = Math.min(6.5, Math.max(1.05, 900 / span)) * (hand ? 1.08 : 1);
  return { lo, hi, pxPerDay, months: pxPerDay * 30.44 >= 34 };
}

/** The month and quarter ticks across [lo, hi): what the header writes. */
export function ganttTicks(axis: GanttAxis): { months: Array<{ day: number; label: string }>; quarters: Array<{ day: number; label: string }> } {
  const months: Array<{ day: number; label: string }> = [];
  const quarters: Array<{ day: number; label: string }> = [];
  for (let d = axis.lo; d < axis.hi; ) {
    const date = new Date(d * DAY);
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    if (axis.months) months.push({ day: d, label: monthName(date, 'short', { timeZone: 'UTC' }) });
    if (m % 3 === 0 || d === axis.lo) quarters.push({ day: d, label: `Q${Math.floor(m / 3) + 1} ${y}` });
    d = monthStart(y, m + 1);
  }
  return { months, quarters };
}

export const dayWords = (day: number) => {
  const d = new Date(day * DAY);
  return dateMedium(d, { timeZone: 'UTC' });
};

/** A day number as an ISO day, for the text alternative. */
export function ganttDayIso(day: number): string {
  return new Date(day * DAY).toISOString().slice(0, 10);
}

/** A forecast's end, as the month it falls in: a forecast is never as exact as a day. */
export function monthWords(day: number): string {
  const d = new Date(day * DAY);
  return monthYear(d, { width: 'short', timeZone: 'UTC' });
}

